import { useState, useEffect, useCallback } from 'react';
import { signIn, signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);

  const checkUser = useCallback(async () => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      
      // Get user groups from the ID token
      const groups = session.tokens?.idToken?.payload?.['cognito:groups'] || [];
      const adminStatus = groups.includes('Admin');
      
      setUser(currentUser);
      setIsAdmin(adminStatus);
      setError(null);
    } catch (err) {
      setUser(null);
      setIsAdmin(false);
      if (err.name !== 'UserUnAuthenticatedException') {
        console.error('Auth error:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkUser();
  }, [checkUser]);

  const login = async (email, password) => {
    try {
      setError(null);
      const result = await signIn({ username: email, password });
      
      if (result.isSignedIn) {
        await checkUser();
        return { success: true };
      }
      
      return { success: false, nextStep: result.nextStep };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      setIsAdmin(false);
    } catch (err) {
      console.error('Logout error:', err);
      throw err;
    }
  };

  const getAccessToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString();
    } catch (err) {
      console.error('Error getting access token:', err);
      return null;
    }
  };

  return {
    user,
    loading,
    isAdmin,
    error,
    login,
    logout,
    getAccessToken,
    checkUser,
  };
}
